/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* Licensed under the MIT License. See LICENSE.md in the project root for license terms.
*--------------------------------------------------------------------------------------------*/

import { Emitter, IEvent, Uri } from "monaco-editor/esm/vs/editor/editor.api";
import { IAnnotation, IAnnotationData, IAnnotationUpdateData } from "../../types/Annotation";
import { Annotation } from "./Annotation";

export interface IAnnotationsChangeEvent {
  added?: Array<IAnnotation>;
  removed?: Array<IAnnotation>;
  changed?: Array<IAnnotation>;
}

export class AnnotationService {
  private _annotations: Annotation[];
  private readonly _onDidChangeAnnotations: Emitter<IAnnotationsChangeEvent | undefined> = new Emitter<IAnnotationsChangeEvent | undefined>()

  public constructor() {
    this._annotations = [];
  }

  public get onDidChangeAnnotations(): IEvent<IAnnotationsChangeEvent | undefined> {
    return this._onDidChangeAnnotations.event;
  }

  public getAnnotations(filter?: { uri?: Uri, lineNumber?: number, column?: number, endLineNumber?: number, endColumnNumber?: number }): IAnnotation[] {
    if (filter) {
      const uriStr = filter.uri ? filter.uri.toString() : undefined;
      return this._annotations.filter((anno) => {
        if (uriStr && anno.uri.toString() !== uriStr) {
          return false;
        }
        if (filter.lineNumber && anno.lineNumber !== filter.lineNumber) {
          return false;
        }
        if (filter.column && anno.column !== filter.column) {
          return false;
        }
        if (filter.endLineNumber && anno.endLineNumber !== filter.endLineNumber) {
          return false;
        }
        if (filter.endColumnNumber && anno.endColumn !== filter.endColumnNumber) {
          return false;
        }

        return true;
      });
    }

    return this._annotations;
  }

  public addAnnotations(uri: Uri, rawData: IAnnotationData[], fireEvent: boolean = true): IAnnotation[] {
    const newAnnotations = rawData.map((rawAnno) => new Annotation(rawAnno.options, uri, rawAnno.lineNumber, rawAnno.endLineNumber, rawAnno.column, rawAnno.endColumn));
    this._annotations = this._annotations.concat(newAnnotations);

    if (fireEvent) {
      this._onDidChangeAnnotations.fire({ added: newAnnotations });
    }

    return newAnnotations;
  }

  public removeAnnotations(toRemove: IAnnotation[]): void {
    this._annotations = this._annotations.filter((anno) => !toRemove.some((toRemove) => toRemove.id === anno.id));
    this._onDidChangeAnnotations.fire({ removed: toRemove });
  }

  public updateAnnotations(data: Map<string, IAnnotationUpdateData>): void {
    const updated: IAnnotation[] = [];
    this._annotations.forEach(anno => {
      const annoData = data.get(anno.id);
      if (annoData) {
        anno.update(annoData);
        updated.push(anno);
      }
    });
    this._onDidChangeAnnotations.fire({ changed: updated });
  }

}